/**
 * <product-content-slider> — single, global slider element.
 *
 * Supersedes the previous three implementations (assets/0-slider.js
 * `newslider-component`, and the duplicate `product-content-slider` classes that
 * lived in assets/0-product-content.js + assets/look-page.js). Loaded once,
 * globally, from layout/theme.liquid.
 *
 * Works with BOTH markup conventions so legacy callers keep working untouched:
 *   - New (snippets/product-slider.liquid): [data-slider-track] / [data-slider-prev|next]
 *   - Legacy (tech block, look gallery): .product-content__slider + .product-content__slider-arrow--left|right
 *
 * Optional editor-driven filter/sort (new markup only; opt-in via host data-*):
 *   data-sort-mode="default|bestseller|score"
 *       bestseller → data-bestseller desc, data-availability-score tiebreak
 *       score      → data-availability-score desc, data-bestseller tiebreak
 *   data-hide-low-availability="true"        drops out-of-stock + low-availability items
 *   data-low-availability-strictness="standard|strict"
 *       standard → drop data-availability-score <= 1 (OOS + score 1)
 *       strict   → drop data-availability-score <= 2 (OOS + scores 1-2)
 * All read attributes that snippets/product-card.liquid already emits.
 */
if (!customElements.get('product-content-slider')) {
  const TRACK_SELECTOR = '[data-slider-track], .slider__track, .product-content__slider, .look-gallery__track';
  const PREV_SELECTOR = '[data-slider-prev], .slider__arrow--prev, .product-content__slider-arrow--left';
  const NEXT_SELECTOR = '[data-slider-next], .slider__arrow--next, .product-content__slider-arrow--right';

  const num = (el, attr, fallback) => {
    const v = el?.getAttribute(attr);
    return v === null || v === undefined || v === '' ? fallback : Number(v);
  };

  const throttle = (fn, wait) => {
    let last = 0;
    let scheduled = false;
    return () => {
      const now = Date.now();
      if (now - last >= wait) {
        last = now;
        fn();
      } else if (!scheduled) {
        scheduled = true;
        setTimeout(() => {
          scheduled = false;
          last = Date.now();
          fn();
        }, wait - (now - last));
      }
    };
  };

  class ProductContentSlider extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this.track = this.querySelector(TRACK_SELECTOR);
      if (!this.track) return;
      this._mounted = true;

      this.prevBtn = this.querySelector(PREV_SELECTOR);
      this.nextBtn = this.querySelector(NEXT_SELECTOR);

      this.#filterSort();

      this.prevBtn?.addEventListener('click', () => this.#scrollByPage(-1));
      this.nextBtn?.addEventListener('click', () => this.#scrollByPage(1));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.#scrollByPage(-1);
        else if (event.key === 'ArrowRight') this.#scrollByPage(1);
      });

      // Position indicator: EITHER dots (data-dots) OR the custom scroll-progress bar
      // — never both. Only for the unified `.product-slider` markup (legacy look/tech
      // sliders keep their own). Dots mirror the sections that used Swiper pagination;
      // the bar (iOS-Safari-safe, always visible while overflowing) covers the rest.
      if (this.classList.contains('product-slider')) {
        if (this.hasAttribute('data-dots')) {
          this.#buildDots();
        } else {
          this.progress = document.createElement('div');
          this.progress.className = 'slider__progress';
          this.progress.setAttribute('aria-hidden', 'true');
          this.progressThumb = document.createElement('div');
          this.progressThumb.className = 'slider__progress-thumb';
          this.progress.appendChild(this.progressThumb);
          this.track.insertAdjacentElement('afterend', this.progress);
          // Thumb movement on its OWN listener, coalesced to one transform write per
          // animation frame — smooth, and never on the throttled arrow-nav path.
          this._onProgressScroll = () => {
            if (this._progressTick) return;
            this._progressTick = true;
            requestAnimationFrame(() => {
              this._progressTick = false;
              this.#moveProgress();
            });
          };
          this.track.addEventListener('scroll', this._onProgressScroll, { passive: true });
        }
      }

      this._onScroll = throttle(() => this.#updateNav(), 100);
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this._onResize = throttle(() => {
        this.#updateNav();
        this.#layoutProgress();
      }, 150);
      window.addEventListener('resize', this._onResize, { passive: true });

      this.#updateNav();
      this.#layoutProgress();

      // Opt-in (host sets data-observe-track): re-evaluate arrows + progress when the
      // track's contents change at runtime — e.g. the Featured Lookbook category filter
      // reorders/hides cards. Scoped to opted-in hosts ONLY, so no other slider pays for
      // it and nothing changes for them. Fully guarded — must never throw.
      if (this.hasAttribute('data-observe-track')) this.#observeTrack();
    }

    disconnectedCallback() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._trackObserver) { try { this._trackObserver.disconnect(); } catch (e) { /* noop */ } }
    }

    #observeTrack() {
      try {
        if (typeof MutationObserver === 'undefined' || !this.track) return;
        const reeval = throttle(() => {
          try {
            this.#updateNav();
            this.#layoutProgress();
          } catch (e) { /* a runtime re-eval must never break the page */ }
        }, 100);
        this._trackObserver = new MutationObserver(reeval);
        this._trackObserver.observe(this.track, {
          childList: true,        // filter reorders / adds / removes cards
          subtree: true,          // + catches [hidden] toggled on nested cards
          attributeFilter: ['hidden'],
        });
      } catch (e) { /* MutationObserver unsupported or failed — degrade silently */ }
    }

    #filterSort() {
      const mode = this.dataset.sortMode || 'default';
      const hideLow = this.dataset.hideLowAvailability === 'true';
      // Nothing to do here when neither is active — Liquid already rendered the
      // exact (already-capped) set in that case.
      if (mode === 'default' && !hideLow) return;

      if (hideLow) {
        // Score 0 = out of stock, 1 = only one variant left, 2 = low.
        // 'strict' also drops score 2; 'standard' keeps it. Missing attr → keep.
        const threshold = this.dataset.lowAvailabilityStrictness === 'strict' ? 2 : 1;
        [...this.track.children].forEach((el) => {
          if (num(el, 'data-availability-score', 3) <= threshold) el.remove();
        });
      }

      if (mode === 'bestseller' || mode === 'score') {
        // bestseller: rank by sales, break ties by availability.
        // score: rank by availability, break ties by sales (score-3 group ordered by bestseller).
        const byBestseller = (a, b) =>
          num(b, 'data-bestseller', 0) - num(a, 'data-bestseller', 0) ||
          num(b, 'data-availability-score', 2) - num(a, 'data-availability-score', 2);
        const byScore = (a, b) =>
          num(b, 'data-availability-score', 2) - num(a, 'data-availability-score', 2) ||
          num(b, 'data-bestseller', 0) - num(a, 'data-bestseller', 0);
        [...this.track.children]
          .sort(mode === 'score' ? byScore : byBestseller)
          .forEach((el) => this.track.appendChild(el));
      }

      // Cap AFTER filtering + sorting: the snippet over-renders the full set when
      // sort/filter is active, so the limit must be applied here, not in Liquid.
      const max = parseInt(this.dataset.maxItems, 10);
      if (max > 0) {
        [...this.track.children].slice(max).forEach((el) => el.remove());
      }
    }

    #scrollByPage(direction) {
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.9, behavior: 'smooth' });
    }

    #updateNav() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      const overflow = max > 2;
      // Inline display so it cooperates with both the new per-breakpoint
      // arrow-hide classes and the legacy sliders' own arrow CSS (whose mobile
      // `display:none !important` still wins when we ask to show).
      this.#toggleArrow(this.prevBtn, overflow && x > 1);
      this.#toggleArrow(this.nextBtn, overflow && x < max - 1);
      if (this.dots) this.#updateDots();
    }

    /* ── Dots (Swiper-pagination replacement) ──────────────────────────────────
       One dot per slide; click scrolls to it; active = nearest-to-left slide. */
    #buildDots() {
      const slides = [...this.track.children];
      if (slides.length < 2) return;
      this.dotsEl = document.createElement('div');
      this.dotsEl.className = 'slider__dots';
      this.dots = slides.map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'slider__dot';
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.addEventListener('click', () => this.#scrollToSlide(i));
        this.dotsEl.appendChild(b);
        return b;
      });
      this.track.insertAdjacentElement('afterend', this.dotsEl);
      this.#updateDots();
    }

    #scrollToSlide(i) {
      const el = this.track.children[i];
      if (!el) return;
      const delta = el.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
      this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: 'smooth' });
    }

    #updateDots() {
      if (!this.dots) return;
      const tLeft = this.track.getBoundingClientRect().left;
      let active = 0;
      let best = Infinity;
      [...this.track.children].forEach((c, i) => {
        const d = Math.abs(c.getBoundingClientRect().left - tLeft);
        if (d < best) { best = d; active = i; }
      });
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
    }

    // Layout-time only (connect + resize): set the thumb's FIXED width once and
    // toggle visibility. Cache the scroll range + travel for #moveProgress.
    #layoutProgress() {
      if (!this.progress) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 2) {
        this.progress.classList.remove('is-visible');
        this._progressMax = 0;
        return;
      }
      this.progress.classList.add('is-visible');
      const barW = this.progress.clientWidth;
      const ratio = this.track.clientWidth / this.track.scrollWidth;
      const thumbW = Math.max(24, Math.round(ratio * barW));
      this.progressThumb.style.width = thumbW + 'px';
      this._progressTravel = barW - thumbW;
      this._progressMax = max;
      this.#moveProgress();
    }

    // Scroll / slide-change: move the thumb with ONLY a composited transform.
    #moveProgress() {
      if (!this._progressMax || !this.progressThumb) return;
      const p = this.track.scrollLeft / this._progressMax;
      this.progressThumb.style.transform = `translate3d(${p * this._progressTravel}px, 0, 0)`;
    }

    #toggleArrow(btn, show) {
      if (btn) btn.style.display = show ? 'flex' : 'none';
    }
  }

  customElements.define('product-content-slider', ProductContentSlider);
  // Expose the base so specialized sliders (e.g. <gallery-slider>) can extend it and
  // reuse the arrow/keyboard/scroll-nav logic instead of duplicating it.
  window.ProductContentSlider = ProductContentSlider;
}
